import { useQuery } from '@tanstack/react-query'
import { Company } from 'interfaces/company'
import { PicklePlan } from 'interfaces/pickle-plan'
import { PickleSubscription } from 'interfaces/pickle-subscription'
import { requestQuery } from 'lib/api/fetch-api'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'

type BillingPlansContextType = {
  company: Company
  currency: Company['currency']
  currentSubscriptions?: SubscriptionsType
  period: string
  plans: PicklePlan[]
  setPeriod: (period: string) => void
  selectedPlans?: PlansType
  updateSelectedPlans: (plan: PicklePlan, type: 'creative' | 'platform' | 'professional') => void
}

export type PlansType = {
  creativePlan?: PicklePlan
  platformPlan?: PicklePlan
  professionalPlan?: PicklePlan
}

export type SubscriptionsType = {
  creativeSubscription?: PickleSubscription
  platformSubscription?: PickleSubscription
  professionalSubscription?: PickleSubscription
}

export const BillingPlansContext = createContext<BillingPlansContextType>({
  company: {} as Company,
  currency: 'USD',
  period: 'monthly',
  plans: [],
  setPeriod: () => {},
  updateSelectedPlans: () => {},
})

export const useBillingPlans = () => {
  const context = useContext(BillingPlansContext)
  if (!context) {
    throw new Error('useBillingPlans must be used within a BillingPlansProvider')
  }
  return context
}

export const BillingPlansProvider = ({ children, company }: { children: React.ReactNode; company: Company }) => {
  const { data, isSuccess } = useQuery({
    queryKey: ['getPicklePlans'],
    queryFn: requestQuery({
      endpoint: 'getPicklePlans',
    }),
    staleTime: 30000,
  })

  const currency = company.currency

  const picklePlans = isSuccess ? data.plans : []

  const currentSubscriptions = useMemo(
    () => ({
      creativeSubscription: company.pickleSubscriptionsStillActive.find((sub) => sub.picklePlan?.creativeServices),
      platformSubscription: company.pickleSubscriptionsStillActive.find((sub) => sub.picklePlan?.platform),
      professionalSubscription: company.pickleSubscriptionsStillActive.find(
        (sub) => sub.picklePlan?.professionalServices,
      ),
    }),
    [company.pickleSubscriptionsStillActive],
  )
  const currentPeriod = currentSubscriptions.platformSubscription?.picklePlan?.period || 'monthly'

  const [period, setPeriod] = useState<string>(currentPeriod)

  const [selectedPlans, setSelectedPlans] = useState<PlansType>({
    creativePlan: currentSubscriptions.creativeSubscription?.picklePlan,
    platformPlan: currentSubscriptions.platformSubscription?.picklePlan,
    professionalPlan: currentSubscriptions.professionalSubscription?.picklePlan,
  })

  const updateSelectedPlans = useCallback(
    (plan: PicklePlan, type: 'creative' | 'platform' | 'professional') => {
      setSelectedPlans((prevPlans) => ({
        ...prevPlans,
        [`${type}Plan`]: plan,
      }))
    },
    [setSelectedPlans],
  )

  const plans = picklePlans.filter((plan) => plan.period === period)

  const value = useMemo(
    () => ({
      company,
      currency,
      currentSubscriptions,
      period,
      plans,
      setPeriod,
      selectedPlans,
      updateSelectedPlans,
    }),
    [company, currency, currentSubscriptions, period, plans, selectedPlans, updateSelectedPlans],
  )

  return <BillingPlansContext.Provider value={value}>{children}</BillingPlansContext.Provider>
}
